import { forwardRef, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  AutocompleteItem,
  Loader,
  SelectItemProps,
  Table,
  ScrollArea,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons';
import { useIntlLight } from '../../../i18n';
import { useSearchOmvqVeterinarians } from '../../../../modules/veterinary/hooks/useSearchOmvqVeterinariansQuery';
import { VeterinarianStatus } from '../../../../types';

export interface OmvqVeterinarianSearchSelectProps {
  onChange?(value: string): void;
  onSelect?: (value: AutocompleteItem | null) => void;
  disabled?: boolean;
  defaultValue?: string;
  name?: string;
  value?: string;
  limit?: number;
  activeOnly?: boolean;
}

interface AutoCompleteItemProps extends SelectItemProps {
  name: string;
  permitNumber: string;
  clinics: string[];
  iri: string;
}

const AutoCompleteItem = forwardRef<HTMLTableRowElement, AutoCompleteItemProps>(
  ({ value, name, permitNumber, clinics, iri, ...others }: AutoCompleteItemProps, ref) => (
    <tr ref={ref} {...others} style={{ borderBottom: '1px solid #e9ecef' }}>
      <td style={{ padding: '8px', width: '300px' }}>{name}</td>
      <td style={{ padding: '8px', width: '600px' }}>
        <ul style={{ paddingInline: '18px', margin: 0 }}>
          {clinics.map((item: string) => (
            <li key={`${iri}-${item}`}>{item}</li>
          ))}
        </ul>
      </td>
      <td style={{ padding: '8px', width: '90px' }}>{permitNumber}</td>
    </tr>
  )
);

const OmvqVeterinarianSearchSelect = ({
  value,
  onChange,
  onSelect,
  name,
  defaultValue,
  disabled = false,
  limit,
  activeOnly = false,
}: OmvqVeterinarianSearchSelectProps) => {
  const { t } = useIntlLight();
  const { searchResult, isFetching, search, clearSearchResult } = useSearchOmvqVeterinarians();

  const [searchQuery, setSearchQuery] = useState<string>();
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 200);

  const [isItemSelected, setIsItemSelected] = useState(false);

  useEffect(() => {
    if (debouncedSearchQuery) {
      search(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, search]);

  useEffect(() => {
    if (
      (!debouncedSearchQuery || debouncedSearchQuery?.length <= 2) &&
      searchResult.items.length >= 1
    ) {
      clearSearchResult();
    }
  }, [debouncedSearchQuery, clearSearchResult, searchResult]);

  useEffect(() => {
    if (!isItemSelected) setSearchQuery(value);
  }, [value, setSearchQuery, isItemSelected]);

  const options = useMemo(() => {
    return searchResult.items
      .filter((item: { status?: string }) =>
        activeOnly ? item.status === VeterinarianStatus.ACTIVE : true
      )
      .map(
        (item: {
          '@id': string;
          id: number;
          name: string;
          clinics: string[];
          permitNumber: string;
          status?: string;
        }) => {
          const { '@id': _ignored, ...rest } = item;
          return {
            value: item.name,
            label: item.name,
            iri: item['@id'],
            ...rest,
          };
        }
      );
  }, [searchResult, activeOnly]);

  const handleAutoCompleteSelect = async (value: AutocompleteItem) => {
    if (onSelect) onSelect(value);
  };

  return (
    <Autocomplete
      dropdownPosition="bottom"
      icon={<IconSearch />}
      label={t('producerRequestAccess_editSiteForm_omvqVeterinarians_label')}
      placeholder={t('producerRequestAccess_associationUserForm_veterinarians_placeholder')}
      rightSection={isFetching ? <Loader variant="oval" size="xs" /> : undefined}
      itemComponent={AutoCompleteItem}
      filter={() => true}
      onChange={(value: string) => {
        onChange?.(value);
        if (!name && !onChange) {
          setSearchQuery(value);
        }
        setIsItemSelected(false);
        if (onSelect) {
          onSelect(null);
        }
      }}
      onItemSubmit={(item: AutocompleteItem) => {
        setIsItemSelected(true);
        handleAutoCompleteSelect(item);
      }}
      data={options}
      disabled={disabled}
      defaultValue={defaultValue}
      name={name}
      value={value}
      limit={limit ?? options.length}
      dropdownComponent={(props: { children: React.ReactNode }) => (
        <ScrollArea.Autosize maxHeight={400} type="auto">
          <Table style={{ width: '100%', tableLayout: 'fixed' }}>{props.children}</Table>
        </ScrollArea.Autosize>
      )}
    />
  );
};

export default OmvqVeterinarianSearchSelect;
